import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import "./dashboardmainpageprtable.css";
import { useTranslation } from "react-i18next";
import JobExpandedTable from "../Jobs/JobsTable/JobExpandedTable";
import CustomPagination from "../CustomPagination/CustomPagination";
import CSVDownloadButton from "../CsvDownload/CsvDownload";
import PrDetailsUnderJobs from "../../Models/PrDetailsUnderJobs/PrDetailsUnderJobs";
import PageLoader from "../../Models/Pageloader/PageLoader";
import { DateGenerator } from "../../Utils/Utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchPrList } from "../../Store/Actions/vendor_dashboard";

const customStyles = {
  headCells: {
    style: {
      backgroundColor: "#103A5D",
      color: "white",
      fontSize: "14px",
      fontFamily: "Sarabun",
      fontStyle: "normal",
      fontWeight: 400,
    },
  },
};

function getGroupName(filterList = {}, groupName) {
  if (!groupName) return null;

  const combinedList = [
    ...(filterList.goods || []),
    ...(filterList.services || []),
    ...(filterList.project || []),
  ];

  const match = combinedList.find(
    (item) => item?.purchasinggroup_name?.trim?.() === groupName.trim()
  );

  return match?.meaning?.trim?.() || null;
}

const ExpandedComponent = ({ data }) => (
  <pre>
    <JobExpandedTable data={data.JobPrDetails} />
  </pre>
);

const DashboardPrTable = ({ error, loading }) => {
  const filterList = useSelector(
    (state) => state?.VendorJobsReducer?.filterList
  );
  const dispatch = useDispatch();
  const { dashboardPRList: prData } = useSelector(
    (state) => state.VendorDashboardReducer || {}
  );

  const { t } = useTranslation();
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [count, setCount] = useState(0);
  const [searchValues, setSearchValues] = useState({});

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const filteredSearchValues = Object.entries(searchValues).reduce(
        (acc, [key, value]) => {
          if (value && value.trim() !== "") {
            acc[key] = value;
          }
          return acc;
        },
        {}
      );
      dispatch(fetchPrList(currentPage, itemsPerPage, filteredSearchValues));
    }, 800);
    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, itemsPerPage, searchValues, dispatch]);

  useEffect(() => {
    if (prData?.data) {
      setData(prData?.data || []);
      setCount(prData?.TotalCount || 0);
    }
  }, [prData]);

  const getColumnWidth = (columnId) => {
    const widths = {
      Location: "10%",
      PRNo: "10%",
      PRTitle: "12%",
      PurchaseGroup: "12%",
      BiddingEnd: "11%",
      DeliveryStart: "11%",
      Buyer: "10%",
      Status: "12%",
      Action: "12%",
    };
    return widths[columnId] || "auto";
  };

  const columns = [
    {
      id: "PR_Company",
      name: `${t("Location")}`,
      selector: (row) => row?.PR_Company || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("Location"),
      placeholder: "MTP1",
    },
    {
      id: "PR_ID",
      name: `${t("PR No")}`,
      selector: (row) =>
        (row?.JobPrDetails && row?.JobPrDetails?.length > 0
          ? row?.JobPrDetails?.length > 1
            ? `Combined (${row?.JobPrDetails?.length})`
            : row?.JobPrDetails[0]?.PrSapDetail?.PR_ID
          : "") || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("PRNo"),
      placeholder: "6000070668",
    },
    {
      id: "PrTitle",
      name: `${t("PR Title")}`,
      selector: (row) => row?.PrTitle || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("PRTitle"),
      placeholder: "search title",
    },
    {
      id: "PR_Group",
      name: `${t("Purchase Group")}`,
      selector: (row) =>
        (row?.JobPrDetails && row?.JobPrDetails?.length > 0
          ? row?.JobPrDetails?.length > 1
            ? ``
            : getGroupName(
                filterList,
                row?.JobPrDetails[0]?.PrSapDetail?.PR_Group
              )
          : "") || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("PurchaseGroup"),
      placeholder: "ELECTRICAL GOODS",
    },
    {
      id: "BiddingDate",
      name: "Bidding End",
      selector: (row) => DateGenerator(row?.BiddingDate) || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("BiddingEnd"),
      placeholder: "yyyy-mm-dd",
    },
    {
      id: "DeliveryDate",
      name: "Delivery Start",
      selector: (row) => DateGenerator(row?.DeliveryDate) || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("DeliveryStart"),
      placeholder: "yyyy-mm-dd",
    },
    {
      id: "BuyerName",
      name: "Buyer",
      selector: (row) => row?.BuyerName || "",
      sortable: true,
      searchable: true,
      width: getColumnWidth("Buyer"),
      placeholder: "Admin",
    },
    {
      id: "Status",
      name: `${t("Status")}`,
      cell: (row) => {
        const isFinalCall = row?.finalCallAvailable === 1 
        let statusText = "";
        let statusClass = "";
        if (row?.jobVendorStatus === 1) {
          statusText = "Awarded";
          statusClass = "Awarded";
        } else if (row?.jobVendorStatus === 2) {
          statusText = "Declined";
          statusClass = "status-declined";
        }
         else if (row?.jobVendorStatus === 3) {
          statusText = "Submitted";
          statusClass = "status-submitted-final-call";
        }
        else if (isFinalCall && row?.jobVendorStatus!== 3) {
          statusText = "Final Price";
          statusClass = "status-final-call";
        } else if (row?.jobVendorStatus === 0) {
          statusText = "Bidding";
          statusClass = "status-bidding";
        }

        return (
          <button className={`dashboard-main-page-award ${statusClass} default-cursor`}>
            {statusText}
          </button>
        );
      },
      searchable: false,
      width: getColumnWidth("Status"),
    },
    {
      id: "Action",
      name: `${t("Action")}`,
      cell: (row) => (
        <button
          className="dashboard-main-page-pr-view"
          onClick={() => {
            handleViewPr(row);
          }}
        >
          View PR
        </button>
      ),
      searchable: false,
      width: getColumnWidth("Action"),
    },
  ];

  const handleViewPr = (result) => {
    setSelectedRowData(result);
    setIsModalOpen(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDropdownChange = (e) => {
    const selectedItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(selectedItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchChange = (event, columnId) => {
    setCurrentPage(1);
    const { value } = event.target;
    setSearchValues((prevSearchValues) => ({
      ...prevSearchValues,
      [columnId]: value,
    }));
  };

  const displayedRange = {
    start: (currentPage - 1) * itemsPerPage + 1,
    end: Math.min(currentPage * itemsPerPage, count),
    total: count,
  };

  const headerNames = columns.map((column) => column?.name || "");

  const csvData = (data || []).map((item) =>
    columns.reduce((row, column) => {
      if (column.id === "Status") {
        row[column.name] =
          item?.jobVendorStatus === 0
            ? "Bidding"
            : item?.jobVendorStatus === 1
            ? "Awarded"
            : item?.jobVendorStatus === 2
            ? "Declined"
            : "";
      } else {
        row[column.name] = column.selector
          ? column.selector(item)
          : item[column.name] || "";
      }
      return row;
    }, {})
  );

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      {loading && <PageLoader />}
      {isModalOpen && (
        <PrDetailsUnderJobs
          isOpen={isModalOpen}
          closeModal={closeModal}
          formTitle="PR detail"
          data={selectedRowData}
        />
      )}
      <div className="dahsboard-main-page-tables">
        <div className="d-flex justify-content-between align-items-center p-3">
          <div>
            <h5 className="dahsboard-main-page-top-head-name">{t("PR")}</h5>
            <div className="">
              <label className="show-po-table-top" htmlFor="">
                {t("Show")}
              </label>
              <select
                className="show-select-options px-2 mx-2"
                id=""
                name=""
                value={itemsPerPage}
                onChange={handleDropdownChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="100">100</option>
              </select>
              <label className="entries-po-table-top">{t("Entries")}</label>
            </div>
          </div>
          <div>
            <CSVDownloadButton
              data={csvData}
              headers={headerNames}
              filename="pr_table_data.csv"
            />
          </div>
        </div>
        <div className="pt-3" style={{ position: "relative" }}>
          <DataTable
            className="vendor-dashboard-pr-table"
            columns={columns}
            data={data}
            customStyles={customStyles}
            pagination={false}
            noDataComponent="No records to display"
            expandableRows
            expandableRowDisabled={(row) =>
              !row.JobPrDetails || row?.JobPrDetails?.length < 2
            }
            expandableRowExpanded={(row) => row?.JobPrDetails?.length > 1}
            expandableRowsComponent={ExpandedComponent}
            persistTableHead
          />
          <div
            style={{
              position: "absolute",
              top: "72px",
              left: 0,
              right: 0,
              padding: "8px",
              paddingLeft: "36px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            className="table-search-div"
          >
            {columns.map((column) =>
              column.searchable !== false ? (
                <input
                  key={column.id}
                  type="text"
                  value={searchValues[column.id] || ""}
                  style={{
                    width: column.width,
                    height: "32px",
                    padding: "2px 8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "12px",
                    boxSizing: "border-box",
                  }}
                  placeholder={`${column.placeholder}`}
                  onChange={(event) => handleSearchChange(event, column.id)}
                />
              ) : (
                <div
                  key={`spacer-${column.id}`}
                  style={{
                    width: column.width,
                    minWidth: "60px",
                  }}
                />
              )
            )}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center">
        <p className="table-show-entries">
          {" "}
          Showing {displayedRange.start} to {displayedRange.end} of {count}{" "}
          entries
        </p>
        <CustomPagination
          totalItems={count || 0}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          innerPagination={true}
        />
      </div>
    </>
  );
};

export default DashboardPrTable;
